import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Calendar, Clock, ArrowRight, TrendingUp, Bookmark, Mail } from 'lucide-react';
import { Button } from '../components/ui/Button';
type Post = {
  title: string;
  category: string;
  date: string;
  readTime: string;
  excerpt: string;
  img: string;
  author: string;
  trending?: boolean;
};
const posts: Post[] = [{
  title: "The 2026 Hiring Outlook: Where the Jobs Are (and Where They Aren't)",
  category: 'Industry Trends',
  date: 'May 12, 2026',
  readTime: '8 min read',
  excerpt: 'Our annual outlook breaks down which industries are accelerating hiring in Q2 2026, which are contracting, and what it means for your next move.',
  img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1400&q=80',
  author: 'Glenn M. Rossi',
  trending: true
}, {
  title: 'How AI Resume Screeners Have Changed in 2026 — And How to Beat Them',
  category: 'Resume Tips',
  date: 'May 8, 2026',
  readTime: '7 min read',
  excerpt: "ATS systems have evolved beyond simple keyword matching. Here's what our executive resume team has learned writing for the new generation of AI screeners.",
  img: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Shawn Tate',
  trending: true
}, {
  title: 'Why Executive Search Is Booming Again in 2026',
  category: 'Executive Search',
  date: 'May 3, 2026',
  readTime: '6 min read',
  excerpt: 'After a slowdown in late 2025, C-suite hiring has surged. We unpack the four forces behind the resurgence and what executives should be doing right now.',
  img: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Glenn M. Rossi'
}, {
  title: "LinkedIn's New Algorithm: What Recruiters Actually See in 2026",
  category: 'LinkedIn Tips',
  date: 'Apr 28, 2026',
  readTime: '5 min read',
  excerpt: 'We analyzed 500+ recruiter searches in the last 90 days. The patterns are clear — and most candidates are getting filtered out before they ever know it.',
  img: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Juliette Langford',
  trending: true
}, {
  title: "The Career Transitioner's Playbook: Pivoting Industries After 40",
  category: 'Career Growth',
  date: 'Apr 22, 2026',
  readTime: '9 min read',
  excerpt: 'Three real client stories on how seasoned professionals successfully repositioned themselves into entirely new sectors — and what they all did right.',
  img: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Olivia Grace Thompson'
}, {
  title: 'Remote, Hybrid, or RTO? The 2026 Workplace Reality Check',
  category: 'Job Search',
  date: 'Apr 15, 2026',
  readTime: '6 min read',
  excerpt: "The pendulum has swung again. Here's what our placement data reveals about where employers really stand on remote work — and how to negotiate it.",
  img: 'https://images.unsplash.com/photo-1593642632823-8f78536788c6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Juliette Langford'
}, {
  title: 'Salary Negotiation in a Cooling Market: New Rules for 2026',
  category: 'Career Growth',
  date: 'Apr 8, 2026',
  readTime: '7 min read',
  excerpt: 'The leverage has shifted, but you still have more power than you think. Five updated tactics our placement team uses to secure offers above-band.',
  img: 'https://images.unsplash.com/photo-1554224154-22dec7ec8818?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Shawn Tate'
}, {
  title: 'Top Industries Hiring Talent Sourcers Right Now',
  category: 'Industry Trends',
  date: 'Apr 1, 2026',
  readTime: '5 min read',
  excerpt: 'From climate tech to AI infrastructure, here are the seven sectors where our recruitment team has placed the most candidates in the past quarter.',
  img: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Glenn M. Rossi'
}];
const categories = ['All', 'Industry Trends', 'Executive Search', 'Resume Tips', 'LinkedIn Tips', 'Career Growth', 'Job Search'];
// Map category → accent color for editorial variety
const categoryColors: Record<string, {
  bg: string;
  text: string;
  dot: string;
}> = {
  'Industry Trends': {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-600'
  },
  'Executive Search': {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    dot: 'bg-purple-600'
  },
  'Resume Tips': {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-600'
  },
  'LinkedIn Tips': {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    dot: 'bg-sky-600'
  },
  'Career Growth': {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-600'
  },
  'Job Search': {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    dot: 'bg-rose-600'
  }
};
const getCatColor = (cat: string) => categoryColors[cat] || {
  bg: 'bg-gray-100',
  text: 'text-gray-700',
  dot: 'bg-gray-500'
};
export function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const filtered = posts.filter(p => {
    const matchesCategory = activeCategory === 'All' || p.category === activeCategory;
    const matchesSearch = searchQuery === '' || p.title.toLowerCase().includes(searchQuery.toLowerCase()) || p.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  const [featured, ...rest] = filtered;
  return <div className="pt-20 bg-white" data-id="element-306">
      {/* Hero — editorial masthead */}
      <section className="relative bg-navy-900 text-white overflow-hidden" data-id="element-307">
        {/* Decorative accents */}
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-gold-500/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" data-id="element-308" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-navy-700/30 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl" data-id="element-309" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative" data-id="element-310">
          <div className="max-w-3xl" data-id="element-311">
            <div className="flex items-center gap-3 mb-6" data-id="element-312">
              <div className="w-12 h-px bg-gold-500" data-id="element-313" />
              <span className="text-gold-500 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-314">
                The TalentBridge Journal
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6 leading-[1.1]" data-id="element-315">
              Career intelligence,
              <br data-id="element-316" />
              <span className="text-gold-500 italic" data-id="element-317">delivered weekly.</span>
            </h1>
            <p className="text-lg text-gray-300 mb-10 max-w-xl leading-relaxed" data-id="element-318">
              In-the-trenches insights from our recruiters, placement
              specialists, and executive search team — based on what's actually
              working for clients right now.
            </p>

            {/* Search */}
            <div className="relative max-w-xl" data-id="element-319">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-320" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search articles, topics, authors..." className="w-full pl-14 pr-6 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:bg-white/15 transition-all" data-id="element-321" />
            </div>
          </div>
        </div>
      </section>

      {/* Categories — pill nav */}
      <section className="sticky top-20 z-30 bg-white/90 backdrop-blur-md border-b border-gray-200" data-id="element-322">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 overflow-x-auto" data-id="element-323">
          <div className="flex items-center gap-2 min-w-max" data-id="element-324">
            <span className="text-xs uppercase tracking-widest text-gray-400 font-bold mr-4 hidden md:inline" data-id="element-325">
              Topics
            </span>
            {categories.map(cat => {
            const isActive = activeCategory === cat;
            return <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${isActive ? 'bg-navy-900 text-white shadow-md' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-navy-900'}`} data-id="element-326">
                  {cat}
                </button>;
          })}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featured && <section className="py-16 bg-white" data-id="element-327">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-328">
            <div className="flex items-center gap-3 mb-8" data-id="element-329">
              <TrendingUp className="h-5 w-5 text-gold-500" data-id="element-330" />
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-navy-900" data-id="element-331">
                Featured This Week
              </span>
              <div className="flex-1 h-px bg-gray-200" data-id="element-332" />
            </div>

            <motion.article initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center group cursor-pointer" data-id="element-333">
              {/* Image */}
              <div className="relative overflow-hidden rounded-2xl aspect-[4/3] shadow-2xl" data-id="element-334">
                <img src={featured.img} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-335" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 to-transparent" data-id="element-336" />
                <div className="absolute top-5 left-5 flex items-center gap-2" data-id="element-337">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 ${getCatColor(featured.category).bg} ${getCatColor(featured.category).text} text-xs font-bold uppercase tracking-wider rounded-full shadow-md`} data-id="element-338">
                    <span className={`w-1.5 h-1.5 rounded-full ${getCatColor(featured.category).dot}`} data-id="element-339" />
                    {featured.category}
                  </span>
                  {featured.trending && <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gold-500 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-md" data-id="element-340">
                      <TrendingUp className="h-3 w-3" data-id="element-341" />
                      Trending
                    </span>}
                </div>
              </div>

              {/* Content */}
              <div data-id="element-342">
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-5" data-id="element-343">
                  <span className="flex items-center gap-1.5" data-id="element-344">
                    <Calendar className="h-4 w-4" data-id="element-345" /> {featured.date}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-346" />
                  <span className="flex items-center gap-1.5" data-id="element-347">
                    <Clock className="h-4 w-4" data-id="element-348" /> {featured.readTime}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-navy-900 mb-6 leading-tight group-hover:text-gold-600 transition-colors" data-id="element-349">
                  {featured.title}
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-350">
                  {featured.excerpt}
                </p>
                <div className="flex items-center justify-between" data-id="element-351">
                  <div className="flex items-center gap-3" data-id="element-352">
                    <div className="w-11 h-11 rounded-full bg-navy-900 text-white flex items-center justify-center font-bold text-sm" data-id="element-353">
                      {featured.author.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div data-id="element-354">
                      <div className="font-bold text-navy-900 text-sm" data-id="element-355">
                        {featured.author}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-356">
                        TalentBridge Editor
                      </div>
                    </div>
                  </div>
                  <a href="#" className="inline-flex items-center gap-2 text-navy-900 font-semibold hover:text-gold-600 transition-colors" data-id="element-357">
                    Read story <ArrowRight className="h-4 w-4" data-id="element-358" />
                  </a>
                </div>
              </div>
            </motion.article>
          </div>
        </section>}

      {/* Article Grid */}
      <section className="py-20 bg-gray-50" data-id="element-359">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-360">
          <div className="flex items-end justify-between mb-12" data-id="element-361">
            <div data-id="element-362">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900" data-id="element-363">
                {activeCategory === 'All' ? 'Latest Stories' : activeCategory}
              </h2>
              <p className="text-gray-500 mt-1 text-sm" data-id="element-364">
                {rest.length} article{rest.length === 1 ? '' : 's'} · Updated
                weekly
              </p>
            </div>
          </div>

          {rest.length === 0 ? <div className="bg-white rounded-2xl p-16 text-center border border-gray-100" data-id="element-365">
              <p className="text-gray-500" data-id="element-366">No articles match your search.</p>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-id="element-367">
              {rest.map((post, index) => {
            const cat = getCatColor(post.category);
            return <motion.article key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-gray-200 hover:shadow-xl transition-all duration-300 flex flex-col group cursor-pointer" data-id="element-368">
                    <div className="relative h-52 overflow-hidden" data-id="element-369">
                      <img src={post.img} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-370" />
                      <button className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-navy-900 hover:bg-white shadow-md transition-all" aria-label="Save article" data-id="element-371">
                        <Bookmark className="h-4 w-4" data-id="element-372" />
                      </button>
                      <div className="absolute bottom-4 left-4" data-id="element-373">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${cat.bg} ${cat.text} text-[10px] font-bold uppercase tracking-wider rounded-full shadow-sm`} data-id="element-374">
                          <span className={`w-1.5 h-1.5 rounded-full ${cat.dot}`} data-id="element-375" />
                          {post.category}
                        </span>
                      </div>
                    </div>

                    <div className="p-6 flex-1 flex flex-col" data-id="element-376">
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3" data-id="element-377">
                        <span className="flex items-center gap-1" data-id="element-378">
                          <Calendar className="h-3 w-3" data-id="element-379" /> {post.date}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-380" />
                        <span className="flex items-center gap-1" data-id="element-381">
                          <Clock className="h-3 w-3" data-id="element-382" /> {post.readTime}
                        </span>
                      </div>

                      <h3 className="text-xl font-serif font-bold text-navy-900 mb-3 leading-snug line-clamp-2 group-hover:text-gold-600 transition-colors" data-id="element-383">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-5 line-clamp-3 flex-1 leading-relaxed" data-id="element-384">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-100" data-id="element-385">
                        <div className="flex items-center gap-2" data-id="element-386">
                          <div className="w-7 h-7 rounded-full bg-navy-900 text-white flex items-center justify-center font-bold text-[10px]" data-id="element-387">
                            {post.author.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <span className="text-xs font-medium text-gray-700" data-id="element-388">
                            {post.author}
                          </span>
                        </div>
                        <span className="text-navy-900 group-hover:text-gold-600 transition-colors" data-id="element-389">
                          <ArrowRight className="h-4 w-4" data-id="element-390" />
                        </span>
                      </div>
                    </div>
                  </motion.article>;
          })}
            </div>}
        </div>
      </section>

      {/* Newsletter — editorial sign-up */}
      <section className="py-20 bg-white" data-id="element-391">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-392">
          <div className="bg-navy-900 rounded-3xl p-10 md:p-16 relative overflow-hidden" data-id="element-393">
            {/* Decorative accents */}
            <div className="absolute top-0 right-0 w-72 h-72 bg-gold-500/10 rounded-full translate-x-1/3 -translate-y-1/3 blur-3xl" data-id="element-394" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-navy-700/40 rounded-full -translate-x-1/3 translate-y-1/3 blur-3xl" data-id="element-395" />

            <div className="relative grid grid-cols-1 md:grid-cols-5 gap-10 items-center" data-id="element-396">
              <div className="md:col-span-3" data-id="element-397">
                <div className="flex items-center gap-3 mb-4" data-id="element-398">
                  <Mail className="h-5 w-5 text-gold-500" data-id="element-399" />
                  <span className="text-gold-500 font-semibold tracking-[0.2em] uppercase text-xs" data-id="element-400">
                    The Weekly Bridge
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4 leading-tight" data-id="element-401">
                  Get career intelligence in your inbox every Tuesday.
                </h2>
                <p className="text-gray-300 leading-relaxed" data-id="element-402">
                  Join 12,000+ professionals reading our weekly newsletter on
                  hiring trends, salary data, and what our recruiters are seeing
                  on the ground.
                </p>
              </div>

              <div className="md:col-span-2" data-id="element-403">
                <form onSubmit={e => e.preventDefault()} className="space-y-3" data-id="element-404">
                  <input type="email" placeholder="you@email.com" className="w-full px-5 py-3.5 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-500" data-id="element-405" />
                  <Button variant="gold" fullWidth data-id="element-406">
                    Subscribe — it's free
                  </Button>
                  <p className="text-xs text-gray-400 text-center" data-id="element-407">
                    No spam. Unsubscribe anytime.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}